import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { inr, inrCompact, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { StatusBadge } from "@/components/StatusBadge";
import { Bike, Battery, Wrench, CheckCircle, Wallet } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function EVTym() {
  const [inv, setInv] = useState(null);
  const [assigns, setAssigns] = useState([]);
  const [pays, setPays] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/ev/inventory").then(({ data }) => setInv(data));
    api.get("/ev/assignments").then(({ data }) => setAssigns(data));
    api.get("/ev/payments").then(({ data }) => setPays(data));
    api.get("/ev/deposits").then(({ data }) => setDeposits(data));
  }, []);

  return (
    <div>
      <PageHeader title="EV Tym Rental" subtitle="Fleet inventory, assignments, rental payments & security deposits" />
      {inv && <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KpiCard testId="ev-total" label="Total EV Bikes" value={inv.stats.total} icon={Bike} accent="primary" />
        <KpiCard testId="ev-available" label="Available" value={inv.stats.Available} icon={CheckCircle} accent="emerald" />
        <KpiCard testId="ev-assigned" label="On Rent" value={inv.stats.Assigned} icon={Battery} accent="cyan" />
        <KpiCard testId="ev-maint" label="Maintenance" value={inv.stats["Under Maintenance"]} icon={Wrench} accent="amber" />
        <KpiCard testId="ev-returned" label="Returned" value={inv.stats.Returned} icon={Wallet} accent="violet" />
      </div>}

      <Tabs defaultValue="assignments" className="mt-6">
        <TabsList>
          <TabsTrigger value="assignments" data-testid="ev-tab-assignments">Assignments</TabsTrigger>
          <TabsTrigger value="payments" data-testid="ev-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="deposits" data-testid="ev-tab-deposits">Security Deposits</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="ev-tab-inventory">Inventory</TabsTrigger>
        </TabsList>

        <TabsContent value="assignments" className="mt-4">
          <TableWrap head={["EV ID", "Model", "DP", "Plan", "Rental", "Outstanding", "Status"]}>
            {assigns.map((a) => (
              <TableRow key={a.id} className="cursor-pointer" onClick={() => nav(`/dp/${a.dp_id}`)} data-testid={`ev-assign-${a.ev_id}`}>
                <TableCell className="font-mono text-xs">{a.ev_id}</TableCell><TableCell>{a.model}</TableCell><TableCell className="font-semibold">{a.dp_name}</TableCell>
                <TableCell>{a.rental_plan}</TableCell><TableCell className="tabular">{inr(a.rental_amount)}/{a.frequency}</TableCell>
                <TableCell className="tabular text-rose-500">{inr(a.outstanding || 0)}</TableCell><TableCell><StatusBadge status={a.vehicle_status === "Active" ? "Active" : a.vehicle_status} /></TableCell>
              </TableRow>
            ))}
          </TableWrap>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <TableWrap head={["Date", "EV ID", "DP FE", "Amount", "Method", "Reference", "Status"]}>
            {pays.slice(0, 200).map((p) => (
              <TableRow key={p.id}><TableCell>{fmtDate(p.date)}</TableCell><TableCell className="font-mono text-xs">{p.ev_id}</TableCell><TableCell className="font-mono text-xs">{p.fe_id}</TableCell><TableCell className="tabular">{inr(p.amount)}</TableCell><TableCell>{p.method}</TableCell><TableCell className="font-mono text-xs">{p.reference}</TableCell><TableCell><StatusBadge status={p.status} /></TableCell></TableRow>
            ))}
          </TableWrap>
        </TabsContent>

        <TabsContent value="deposits" className="mt-4">
          <TableWrap head={["DP FE", "EV ID", "Amount", "Deposit Date", "Refundable", "Balance"]}>
            {deposits.map((d) => (
              <TableRow key={d.id}><TableCell className="font-mono text-xs">{d.fe_id}</TableCell><TableCell className="font-mono text-xs">{d.ev_id}</TableCell><TableCell className="tabular font-semibold">{inr(d.amount)}</TableCell><TableCell>{fmtDate(d.deposit_date)}</TableCell><TableCell>{d.refundable ? "Yes" : "No"}</TableCell><TableCell className="tabular">{inr(d.balance)}</TableCell></TableRow>
            ))}
          </TableWrap>
        </TabsContent>

        <TabsContent value="inventory" className="mt-4">
          <TableWrap head={["EV ID", "Reg. No.", "Model", "Battery", "Health", "Status"]}>
            {(inv?.bikes || []).map((b) => (
              <TableRow key={b.id}><TableCell className="font-mono text-xs">{b.ev_id}</TableCell><TableCell className="font-mono text-xs">{b.reg_number}</TableCell><TableCell>{b.model}</TableCell><TableCell className="font-mono text-xs">{b.battery_id}</TableCell><TableCell className="tabular">{b.battery_health}%</TableCell><TableCell><StatusBadge status={b.status} /></TableCell></TableRow>
            ))}
          </TableWrap>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const TableWrap = ({ head, children }) => (
  <div className="overflow-hidden rounded-xl border border-border bg-card">
    <Table><TableHeader><TableRow className="hover:bg-transparent">{head.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader><TableBody>{children}</TableBody></Table>
  </div>
);
